import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/apiConfig';
import { APP_CONFIG } from '../config/appConfig';

// Create axios instance with default timeout
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: APP_CONFIG.api.timeout, // Default timeout, will be updated by settings
});

// Function to update API timeout dynamically
export const updateApiTimeout = (timeout: number) => {
  api.defaults.timeout = timeout;
};

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const message = 
      (error.response?.data as any)?.message || 
      error.message || 
      'An error occurred';

    // Handle 401 Unauthorized
    if (status === 401) {
      localStorage.removeItem('access_token');
      // Don't redirect on public pages (e.g. register page fetches invitation without auth; other providers may fetch and get 401)
      const isPublicPage = window.location.pathname === '/login' || window.location.pathname === '/register';
      if (!isPublicPage) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle 403 Forbidden
    if (status === 403) {
      console.error('Forbidden:', message);
      toast.error(message || 'You don\'t have permission to perform this action.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle 404 Not Found
    if (status === 404) {
      console.error('Not Found:', message);
      toast.error(message || 'Resource not found.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle 500+ Server Errors
    if (status && status >= 500) {
      console.error('Server Error:', message);
      toast.error(message || 'Server error. Please try again later.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle validation errors (400)
    if (status === 400) {
      console.error('Validation Error:', message);
      toast.error(message || 'Validation error.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Default error handling
    console.error('API Error:', message);
    toast.error(message || 'An error occurred.', { duration: 5000 });
    return Promise.reject(error);
  }
);

// Export the instance
export default api;
